/*
 * What the editor's image machinery needs to know about the thing the images
 * belong to - an article, or a release note (S42, 2026-10-12).
 *
 * WHY AN ADAPTER. The markdown editor, the Images card and the page-level preview
 * cache were written for articles, and a single `articleId` prop drove three
 * unrelated things through them at once: where an image is uploaded to and
 * fetched from, which article the link picker leaves out, and whether there is
 * anything to attach to yet. A release note needs the first and the third with a
 * different owner and the second not at all, so the owner became a value of its
 * own - the calls, the ceiling and its message, and a noun for the sentences -
 * and the editor no longer has to know which entity it is editing.
 *
 * Rejected: copying the ~2,000 lines of editor, panel and cache for release notes.
 * Two copies of the paste/drop/upload pipeline, the reference audit and the
 * object-URL rules (each of which shipped a defect once) would be two places to
 * fix the next one.
 *
 * THE KEY IS NAMESPACED - `article:<id>`, `release-note:<id>` - because the
 * preview cache decides whether a consumer may use the page's store by comparing
 * keys. Two GUID spaces compared bare could, in principle, collide; a prefix
 * makes "the same owner" mean the same KIND of owner too. The key is also what
 * the cache is keyed on instead of the adapter object: a page builds its adapter
 * in a useMemo, but a store that re-created itself whenever an adapter's identity
 * changed would revoke every object URL and download every picture again, so the
 * cache reads the functions through a ref and only a new key is a new store.
 *
 * The article signatures stay. MarkdownEditor and ArticleImagesPanel still take
 * `articleId` as the shorthand for `articleImageOwner(api, articleId)`, so every
 * article call site and test is unchanged; an explicit `imageOwner` wins when
 * both are given.
 */

import type {
  AdminArticleImageBulkDeleteResponse,
  AdminArticleImageDeleteResponse,
  AdminArticleImageListResponse,
  AdminArticleImageUploadResponse,
} from '@accotool-kb/shared';
import { ARTICLE_IMAGE_MAX_PER_ARTICLE, RELEASE_NOTE_IMAGE_MAX_PER_NOTE } from '@accotool-kb/shared';
import type { AdminApi, ArticleImageUploadOptions } from '../../api/adminApi.js';
import { rulesEn } from '../../i18n/admin/rules.en.js';

/** The rules in the chosen language, t.admin.rules; English when none is given. */
type Rules = typeof rulesEn;

/** The thing images are attached to, as the editor and the Images card use it. */
export interface ImageOwner {
  /** `article:<id>` or `release-note:<id>` - what the preview cache is keyed on. */
  key: string;
  /** Uploads one file; the response carries the name the server actually stored. */
  upload(file: Blob, options?: ArticleImageUploadOptions): Promise<AdminArticleImageUploadResponse>;
  /** What is attached now, metadata only. */
  list(): Promise<AdminArticleImageListResponse>;
  /** Detaches one image; `acknowledgeReferences` as for deleteArticleImage. */
  remove(
    fileName: string,
    options?: { acknowledgeReferences?: boolean },
  ): Promise<AdminArticleImageDeleteResponse>;
  /** Detaches several images in one all-or-nothing request. */
  removeMany(
    fileNames: string[],
    options?: { acknowledgeReferences?: boolean },
  ): Promise<AdminArticleImageBulkDeleteResponse>;
  /** The bytes as a blob: URL the caller must revoke - see articleImageObjectUrl. */
  objectUrl(fileName: string, signal?: AbortSignal): Promise<string>;
  /** The endpoint path, as the src of last resort. */
  path(fileName: string): string;
  /** How many images the owner may carry; mirrors its database trigger. */
  maxImages: number;
  /** The refusal shown when that ceiling is reached, in the owner's own words. */
  describeCountLimit(rules?: Rules): string;
  /**
   * What the owner is called mid-sentence: 'article', 'release note'. The
   * dictionary's sentences take it as a kind (t.admin.editor, S48).
   */
  noun: string;
}

/** The part of an owner the preview cache needs: who it is, and how to fetch bytes. */
export type ImagePreviewSource = Pick<ImageOwner, 'key' | 'objectUrl'>;

/** The cache key of an article's images. */
export function articleImageOwnerKey(articleId: string): string {
  return `article:${articleId}`;
}

/** The cache key of a release note's images. */
export function releaseNoteImageOwnerKey(releaseNoteId: string): string {
  return `release-note:${releaseNoteId}`;
}

/** An article as an image owner: the article calls on the admin API, bound to its id. */
export function articleImageOwner(api: AdminApi, articleId: string): ImageOwner {
  return {
    key: articleImageOwnerKey(articleId),
    upload: (file, options) => api.uploadArticleImage(articleId, file, options),
    list: () => api.listArticleImages(articleId),
    remove: (fileName, options) => api.deleteArticleImage(articleId, fileName, options),
    removeMany: (fileNames, options) => api.deleteArticleImages(articleId, fileNames, options),
    objectUrl: (fileName, signal) => api.articleImageObjectUrl(articleId, fileName, signal),
    path: (fileName) => api.articleImagePath(articleId, fileName),
    maxImages: ARTICLE_IMAGE_MAX_PER_ARTICLE,
    describeCountLimit: (rules = rulesEn) => rules.articleImageCountLimit,
    noun: 'article',
  };
}

/** A release note as an image owner: its own calls, its own ceiling and message. */
export function releaseNoteImageOwner(api: AdminApi, releaseNoteId: string): ImageOwner {
  return {
    key: releaseNoteImageOwnerKey(releaseNoteId),
    upload: (file, options) => api.uploadReleaseNoteImage(releaseNoteId, file, options),
    list: () => api.listReleaseNoteImages(releaseNoteId),
    remove: (fileName, options) => api.deleteReleaseNoteImage(releaseNoteId, fileName, options),
    removeMany: (fileNames, options) =>
      api.deleteReleaseNoteImages(releaseNoteId, fileNames, options),
    objectUrl: (fileName, signal) => api.releaseNoteImageObjectUrl(releaseNoteId, fileName, signal),
    path: (fileName) => api.releaseNoteImagePath(releaseNoteId, fileName),
    maxImages: RELEASE_NOTE_IMAGE_MAX_PER_NOTE,
    describeCountLimit: (rules = rulesEn) => rules.releaseNoteImageCountLimit,
    noun: 'release note',
  };
}
